import React from 'react';

/**
 * JsonLd component for rendering Schema.org structured data
 * Uses stable IDs to prevent duplicate scripts on route changes
 */
export default function JsonLd({ id, json }) {
  const jsonText = JSON.stringify(json).replace(/</g, '\\u003c').replace(/\u2028/g, '\\u2028').replace(/\u2029/g, '\\u2029');
  return (
    <script
      id={id}
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: jsonText }}
    />
  );
}

/**
 * Organization schema - use in Layout
 */
export function OrganizationSchema() {
  const org = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": "https://enkelenergi.dk/#organization",
    "name": "enkel energi",
    "url": "https://enkelenergi.dk",
    "logo": "https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/77ba4fb0d_d9c1ec595_Enkel_LOGO-281.png",
    "sameAs": [
      "https://dk.linkedin.com/company/enkel-energi",
      "https://www.facebook.com/profile.php?id=61558752606914",
      "https://www.instagram.com/enkel_energi/"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+45 70 60 80 07",
      "contactType": "customer service",
      "email": "hej@enkelenergi.dk",
      "availableLanguage": ["Danish", "English"],
      "hoursAvailable": [
        { "@type": "OpeningHoursSpecification", "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"], "opens": "09:00", "closes": "13:00" }
      ]
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Bryghuspladsen 8",
      "addressLocality": "København K",
      "postalCode": "1473",
      "addressCountry": "DK"
    }
  };
  
  return <JsonLd id="ld-org" json={org} />;
}

/**
 * BreadcrumbList schema
 * @param {Array} items - Array of {name, path} objects
 */
export function BreadcrumbSchema({ items }) {
  if (!items || items.length === 0) return null;
  
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://enkelenergi.dk${item.path}`
    }))
  };
  
  return <JsonLd id="ld-breadcrumbs" json={breadcrumb} />;
}

/**
 * Product schema for charger pages
 * Enhanced with shipping, returns, and availability for rich search results
 */
export function ProductSchema({ name, description, image, price, sku, brand, availability = "InStock" }) {
  const product = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": name,
    "description": description,
    "image": Array.isArray(image) ? image : [image],
    "brand": {
      "@type": "Brand",
      "name": brand
    },
    "sku": sku,
    "offers": {
      "@type": "Offer",
      "url": typeof window !== 'undefined' ? `https://enkelenergi.dk${window.location.pathname.toLowerCase().replace(/\/$/, '') || '/'}` : 'https://enkelenergi.dk/',
      "priceCurrency": "DKK",
      "price": price,
      "availability": `https://schema.org/${availability}`,
      "itemCondition": "https://schema.org/NewCondition",
      "seller": {
        "@type": "Organization",
        "name": "enkel energi"
      },
      "shippingDetails": {
        "@type": "OfferShippingDetails",
        "shippingRate": {
          "@type": "MonetaryAmount",
          "value": "0",
          "currency": "DKK"
        },
        "shippingDestination": {
          "@type": "DefinedRegion",
          "addressCountry": "DK"
        },
        "deliveryTime": {
          "@type": "ShippingDeliveryTime",
          "handlingTime": {
            "@type": "QuantitativeValue",
            "minValue": 1,
            "maxValue": 2,
            "unitCode": "DAY"
          },
          "transitTime": {
            "@type": "QuantitativeValue",
            "minValue": 3,
            "maxValue": 7,
            "unitCode": "DAY"
          }
        }
      },
      "hasMerchantReturnPolicy": {
        "@type": "MerchantReturnPolicy",
        "applicableCountry": "DK",
        "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
        "merchantReturnDays": 14,
        "returnMethod": "https://schema.org/ReturnByMail"
      }
    }
  };
  
  return <JsonLd id="ld-product" json={product} />;
}

/**
 * Service schema for electricity and solar deals
 */
export function ServiceSchema({ name, description, price, priceUnit = "kWh", serviceType, termsUrl }) {
  const service = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": name,
    "description": description,
    "serviceType": serviceType || "Electricity supply",
    "areaServed": {
      "@type": "Country",
      "name": "DK"
    },
    "provider": {
      "@type": "Organization",
      "name": "enkel energi",
      "url": "https://enkelenergi.dk"
    },
    "offers": {
      "@type": "Offer",
      "priceCurrency": "DKK",
      "priceSpecification": {
        "@type": "UnitPriceSpecification",
        "price": price,
        "priceCurrency": "DKK",
        "unitCode": priceUnit
      }
    }
  };
  
  if (termsUrl) {
    service.termsOfService = termsUrl;
  }
  
  return <JsonLd id="ld-service" json={service} />;
}

/**
 * CollectionPage schema for overview pages
 */
export function CollectionSchema({ name, description, items }) {
  const collection = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": name,
    "description": description,
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": items.map((item, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://enkelenergi.dk${item.path}`
      }))
    }
  };
  
  return <JsonLd id="ld-collection" json={collection} />;
}

/**
 * BlogPosting schema for blog articles
 */
export function BlogPostingSchema({ headline, image, datePublished, dateModified, author }) {
  const article = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": headline,
    "image": Array.isArray(image) ? image : [image],
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": author ? "Person" : "Organization",
      "name": author || "enkel energi"
    },
    "publisher": {
      "@type": "Organization",
      "name": "enkel energi",
      "logo": {
        "@type": "ImageObject",
        "url": "https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/77ba4fb0d_d9c1ec595_Enkel_LOGO-281.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": typeof window !== 'undefined' ? `https://enkelenergi.dk${window.location.pathname.toLowerCase().replace(/\/$/, '') || '/'}` : 'https://enkelenergi.dk/'
    }
  };
  
  return <JsonLd id="ld-article" json={article} />;
}

/**
 * Article schema for guides
 */
export function ArticleSchema({ headline, image, datePublished, dateModified, author }) {
  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": headline,
    "image": Array.isArray(image) ? image : [image],
    "datePublished": datePublished,
    "dateModified": dateModified || datePublished,
    "author": {
      "@type": author ? "Person" : "Organization",
      "name": author || "enkel energi"
    },
    "publisher": {
      "@type": "Organization",
      "name": "enkel energi",
      "logo": {
        "@type": "ImageObject",
        "url": "https://base44.app/api/apps/691da83d30e4689f56b0df07/files/mp/public/691da83d30e4689f56b0df07/77ba4fb0d_d9c1ec595_Enkel_LOGO-281.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": typeof window !== 'undefined' ? `https://enkelenergi.dk${window.location.pathname.toLowerCase().replace(/\/$/, '') || '/'}` : 'https://enkelenergi.dk/'
    }
  };
  
  return <JsonLd id="ld-article" json={article} />;
}

/**
 * FAQPage schema
 */
export function FAQSchema({ faqs }) {
  if (!faqs || faqs.length === 0) return null;
  
  const faqPage = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
  
  return <JsonLd id="ld-faq" json={faqPage} />;
}